<template>
  <UiPopup
    :class="$style.root"
    data-testid="idle-popup"
    :classes="{ content: 'IdlePopup__content' }"
    :style="{ '--IdlePopup--duration': `${progressBarDuration}ms` }"
    @close="onContinueClick"
  >
    <div :class="$style.wrapper">
      <h1 :class="[$style.title, 'kiosk-title']">{{ $t('IdlePopup.title') }}</h1>
      <div :class="[$style.caption, 'kiosk-caption']">{{ $t('IdlePopup.caption') }}</div>

      <div :class="$style.actions">
        <span :class="[$style.buttonCancel, 'kiosk-button-inline']" @click="onStartOverClick">
          {{ $t('IdlePopup.buttonRestart') }}
        </span>
        <button type="button" class="kiosk-button" @click="onContinueClick">
          {{ $t('IdlePopup.buttonContinue') }}
        </button>
      </div>

      <div :class="$style.progress">
        <div
          :class="[
            $style.progressInner,
            isProgressbarAnimating && $style.progressInnerAnimating,
          ]"
        />
      </div>
    </div>
  </UiPopup>
</template>


<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { idleWatcher } from '@/services/IdleWatcher';
import { sentryService } from '@/services/telemetry/sentry';
// biome-ignore lint/correctness/noUnusedImports: used via tag <UiPopup> in <template>
import { UiPopup } from '@/ui-kit';

const progressBarDuration = idleWatcher.maxIdlePopupDuration;
const isProgressbarAnimating = ref(false);

let popupShownAt: number | null = null;
let animationTimerId: ReturnType<typeof setTimeout> | null = null;
// Touch-screen double-tap protection: idleWatcher.reset() is async
// (session.end → flush → navigation), and concurrent calls race on
// session cleanup. Once set, intentionally NOT cleared on success — by then
// the component is unmounting and any stray tap should be ignored.
let isStartOverInFlight = false;

function getDisplayDuration(): number {
  return popupShownAt ? Date.now() - popupShownAt : 0;
}

onMounted(() => {
  popupShownAt = Date.now();

  sentryService.addBreadcrumb(
    'idle',
    'Idle popup shown',
    { maxDuration: progressBarDuration, timestamp: popupShownAt },
    'info'
  );
  sentryService.incrementCounter('idle_popup.shown');

  animationTimerId = setTimeout(() => {
    isProgressbarAnimating.value = true;
    animationTimerId = null;
  }, idleWatcher.TICK_INTERVAL);
});

onBeforeUnmount(() => {
  if (animationTimerId !== null) {
    clearTimeout(animationTimerId);
    animationTimerId = null;
  }
});

async function onStartOverClick(): Promise<void> {
  if (isStartOverInFlight) return;
  isStartOverInFlight = true;

  const displayDuration = getDisplayDuration();

  // CRITICAL: execute user action FIRST so Sentry failures can never block it.
  try {
    await idleWatcher.reset();
  } catch (error) {
    sentryService.captureException(error as Error, {
      tags: { component: 'IdlePopup', action: 'start_over' },
      extra: {
        displayDurationMs: displayDuration,
        progressBarDuration,
      },
      level: 'error',
    });
    isStartOverInFlight = false;
    throw error;
  }

  trackIdleChoice('start_over', 'Start Over', displayDuration);
}

function onContinueClick(): void {
  const displayDuration = getDisplayDuration();

  idleWatcher.closeIdlePopup();

  trackIdleChoice('continue', 'Continue', displayDuration);
}

function trackIdleChoice(
  action: 'start_over' | 'continue',
  label: string,
  displayDuration: number
): void {
  sentryService.addBreadcrumb(
    'idle',
    `Idle popup: user clicked ${label}`,
    { action, displayDurationMs: displayDuration },
    'info'
  );
  sentryService.trackMetric(
    'idle_popup.display_duration',
    displayDuration,
    'millisecond',
    { action }
  );
  sentryService.incrementCounter(`idle_popup.${action}`);
}
</script>


<style module>
.root {
  --IdlePopup--duration: 50s;
}

:global(.IdlePopup__content) {
  overflow: hidden;
}

.wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--kiosk-idle-popup-wrapper-padding);
}

.title {
  font-size: var(--kiosk-idle-popup-title-font-size);
}

.caption {
  margin-top: var(--kiosk-idle-popup-caption-margin-top);
  font-size: var(--kiosk-idle-popup-caption-font-size);
  text-align: var(--kiosk-idle-popup-caption-text-align, inherit);
}

.actions {
  display: flex;
  flex-direction: var(--kiosk-idle-popup-actions-direction);
  margin-top: var(--kiosk-idle-popup-actions-margin-top);
  align-items: center;
  gap: var(--kiosk-idle-popup-actions-gap);
}

.buttonCancel {
  margin-right: var(--kiosk-idle-popup-cancel-margin-right);
  font-size: 1.6rem;
}

.progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

.progressInner {
  background: var(--Kiosk--font_color);
  height: 0.56rem;
  width: 100%;
  transition: width var(--IdlePopup--duration) linear;
}

.progressInnerAnimating {
  width: 0;
}
</style>
